import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash, Warning } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TimeInput24h } from '@/components/ui/time-input-24h';
import { DateInputISO } from '@/components/ui/date-input-iso';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  addAvailabilityException,
  deleteAvailabilityException,
  getAvailability,
  setAvailabilityRules,
  type AvailabilityException,
  type AvailabilityRule,
  type SetAvailabilityRuleInput,
} from '@/api/locations';

// LocationAvailabilityFieldset edits the "booked hours" for one location
// (and its zones inherit by default — zone-level rule editing lives behind
// an Override action per row so the common case stays one editor).
//
// Replace-semantics matches the server: this widget shows the full weekly
// set and submits it as one PUT. Per-row save buttons would imply a diff
// the server doesn't compute.

interface Props {
  slug: string;
  token: string | null;
  locationId: string;
}

// LocationAvailabilityFieldsetHandle is the imperative surface the parent
// modal uses to commit buffered changes on its single "Save" submit. The
// fieldset has no inner save button — the user only sees the modal's
// bottom Save / Cancel pair, and that's the only thing that persists
// hours.
export interface LocationAvailabilityFieldsetHandle {
  save: () => Promise<void>;
}

interface RuleDraft {
  id: string; // ephemeral when not yet persisted
  day_of_week: number;
  start_time: string;
  end_time: string;
}

// Stable client-only id for unpersisted drafts.
let draftSeq = 0;
function nextDraftId(): string {
  draftSeq += 1;
  return `draft-${draftSeq}`;
}

function toDraft(r: AvailabilityRule): RuleDraft {
  return {
    id: r.id,
    day_of_week: r.day_of_week,
    start_time: r.start_time,
    end_time: r.end_time,
  };
}

function emptyDraftFor(day: number): RuleDraft {
  return { id: nextDraftId(), day_of_week: day, start_time: '17:00', end_time: '22:00' };
}

export const LocationAvailabilityFieldset = forwardRef<
  LocationAvailabilityFieldsetHandle,
  Props
>(function LocationAvailabilityFieldset({ slug, token, locationId }, ref) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading } = useQuery({
    queryKey: ['locations', slug, locationId, 'availability'],
    queryFn: () => getAvailability(token, slug, locationId),
    enabled: !!locationId,
  });

  // Local draft state: per-day arrays of rule drafts. Seeded from server
  // data on first load; subsequent saves replace it. Once seeded, server
  // re-fetches don't clobber in-progress edits — the user has to save
  // (which triggers an invalidate and a re-seed via this same effect).
  const [drafts, setDrafts] = useState<Record<number, RuleDraft[]> | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    if (drafts !== null) return;
    const byDay: Record<number, RuleDraft[]> = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
    for (const r of data.rules[''] ?? []) {
      byDay[r.day_of_week] = [...(byDay[r.day_of_week] ?? []), toDraft(r)];
    }
    setDrafts(byDay);
  }, [data, drafts]);

  const effectiveDrafts: Record<number, RuleDraft[]> =
    drafts ?? { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };

  function addWindow(day: number) {
    setDrafts((prev) => {
      const base = prev ?? effectiveDrafts;
      return { ...base, [day]: [...(base[day] ?? []), emptyDraftFor(day)] };
    });
  }

  function removeWindow(day: number, id: string) {
    setDrafts((prev) => {
      const base = prev ?? effectiveDrafts;
      return { ...base, [day]: (base[day] ?? []).filter((r) => r.id !== id) };
    });
  }

  function patchWindow(day: number, id: string, patch: Partial<RuleDraft>) {
    setDrafts((prev) => {
      const base = prev ?? effectiveDrafts;
      return {
        ...base,
        [day]: (base[day] ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r)),
      };
    });
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const flat: SetAvailabilityRuleInput[] = [];
      for (let d = 1; d <= 7; d += 1) {
        for (const r of effectiveDrafts[d] ?? []) {
          if (!r.start_time || !r.end_time) continue;
          if (r.end_time <= r.start_time) {
            throw new Error(`time_order:${d}`);
          }
          flat.push({
            day_of_week: r.day_of_week,
            start_time: r.start_time,
            end_time: r.end_time,
          });
        }
      }
      return setAvailabilityRules(token, slug, locationId, null, flat);
    },
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ['locations', slug, locationId, 'availability'] });
    },
    onError: (err) => {
      if (err instanceof Error && err.message.startsWith('time_order:')) {
        setSaveError(t('locations.availability.errors.timeOrder'));
        return;
      }
      setSaveError(t('locations.availability.errors.saveFailed'));
    },
  });

  // Dirty tracking — the modal's bottom Save only needs to hit the
  // availability endpoint when there's actually something to send.
  // Compare current drafts against the server snapshot (data.rules['']).
  const isDirty = useMemo(() => {
    if (!data || !drafts) return false;
    const serverRows = (data.rules[''] ?? [])
      .map((r) => `${r.day_of_week}|${r.start_time}|${r.end_time}`)
      .sort();
    const draftRows: string[] = [];
    for (let d = 1; d <= 7; d += 1) {
      for (const r of drafts[d] ?? []) {
        if (!r.start_time || !r.end_time) continue;
        draftRows.push(`${r.day_of_week}|${r.start_time}|${r.end_time}`);
      }
    }
    draftRows.sort();
    if (serverRows.length !== draftRows.length) return true;
    for (let i = 0; i < serverRows.length; i += 1) {
      if (serverRows[i] !== draftRows[i]) return true;
    }
    return false;
  }, [data, drafts]);

  // Latest values captured in a ref so the imperative save() closure
  // doesn't go stale between renders. useImperativeHandle's deps array
  // would otherwise force the parent to re-grab the ref on every state
  // change, which causes its own bugs.
  const latest = useRef({ isDirty, mutate: saveMut.mutateAsync });
  useEffect(() => {
    latest.current = { isDirty, mutate: saveMut.mutateAsync };
  });

  useImperativeHandle(
    ref,
    () => ({
      save: async () => {
        if (!latest.current.isDirty) return;
        await latest.current.mutate();
      },
    }),
    [],
  );

  // Exceptions — chronological list with an inline add row.
  const exceptions: AvailabilityException[] = useMemo(() => {
    const all: AvailabilityException[] = [];
    if (!data) return all;
    for (const arr of Object.values(data.exceptions)) {
      for (const e of arr) all.push(e);
    }
    return all.sort((a, b) => a.on_date.localeCompare(b.on_date));
  }, [data]);

  const [newExceptionOpen, setNewExceptionOpen] = useState(false);
  const [excDate, setExcDate] = useState('');
  const [excKind, setExcKind] = useState<'closed' | 'hours'>('closed');
  const [excStart, setExcStart] = useState('08:00');
  const [excEnd, setExcEnd] = useState('20:00');
  const [excNote, setExcNote] = useState('');

  const addExcMut = useMutation({
    mutationFn: () =>
      addAvailabilityException(token, slug, locationId, {
        on_date: excDate,
        kind: excKind,
        start_time: excKind === 'hours' ? excStart : null,
        end_time: excKind === 'hours' ? excEnd : null,
        note: excNote,
      }),
    onSuccess: () => {
      setNewExceptionOpen(false);
      setExcDate('');
      setExcNote('');
      qc.invalidateQueries({ queryKey: ['locations', slug, locationId, 'availability'] });
    },
  });

  const deleteExcMut = useMutation({
    mutationFn: (id: string) => deleteAvailabilityException(token, slug, locationId, id, ''),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations', slug, locationId, 'availability'] });
    },
  });

  async function handleDeleteException(e: AvailabilityException) {
    const ok = await confirm({
      title: t('locations.availability.exceptionDeleteConfirmTitle'),
      body: t('locations.availability.exceptionDeleteConfirmBody', { date: e.on_date }),
      confirmLabel: t('common.remove'),
      tone: 'destructive',
    });
    if (!ok) return;
    await deleteExcMut.mutateAsync(e.id);
  }

  const dayLabels = [
    t('locations.availability.day.monday'),
    t('locations.availability.day.tuesday'),
    t('locations.availability.day.wednesday'),
    t('locations.availability.day.thursday'),
    t('locations.availability.day.friday'),
    t('locations.availability.day.saturday'),
    t('locations.availability.day.sunday'),
  ];

  if (isLoading) {
    return (
      <fieldset className="mt-6 rounded-xl border border-border p-4">
        <legend className="px-2 font-heading text-sm font-semibold">
          {t('locations.availability.heading')}
        </legend>
        <p className="mt-2 text-xs text-foreground/60">{t('common.loading')}</p>
      </fieldset>
    );
  }

  const totalRules = (Object.values(effectiveDrafts) as RuleDraft[][]).reduce(
    (acc, arr) => acc + arr.length,
    0,
  );

  return (
    <fieldset className="mt-6 rounded-xl border border-border p-4">
      <legend className="px-2 font-heading text-sm font-semibold">
        {t('locations.availability.heading')}
      </legend>
      <p className="text-xs text-foreground/70">
        {totalRules === 0
          ? t('locations.availability.taglineEmpty')
          : t('locations.availability.tagline')}
      </p>

      <div className="mt-4 space-y-3">
        {[1, 2, 3, 4, 5, 6, 7].map((day) => (
          <div key={day} className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="w-24 shrink-0 pt-2 text-sm font-medium">{dayLabels[day - 1]}</div>
            <div className="flex flex-1 flex-col gap-2">
              {(effectiveDrafts[day] ?? []).length === 0 ? (
                <p className="text-xs text-foreground/50">
                  {t('locations.availability.dayEmpty')}
                </p>
              ) : (
                (effectiveDrafts[day] ?? []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    <TimeInput24h
                      value={r.start_time}
                      onChange={(next) => patchWindow(day, r.id, { start_time: next })}
                      className="w-24"
                      aria-label={t('locations.availability.startTime')}
                    />
                    <span className="text-foreground/50">—</span>
                    <TimeInput24h
                      value={r.end_time}
                      onChange={(next) => patchWindow(day, r.id, { end_time: next })}
                      className="w-24"
                      aria-label={t('locations.availability.endTime')}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('locations.availability.removeWindow')}
                      onClick={() => removeWindow(day, r.id)}
                    >
                      <Trash weight="bold" />
                    </Button>
                  </div>
                ))
              )}
              <div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addWindow(day)}
                >
                  <Plus weight="bold" />
                  {t('locations.availability.addWindow')}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {saveError ? (
        <p role="alert" className="mt-3 flex items-center gap-2 text-sm text-destructive">
          <Warning weight="bold" />
          {saveError}
        </p>
      ) : null}

      <hr className="my-5 border-border" />

      <div>
        <div className="flex items-center justify-between">
          <h4 className="font-heading text-sm font-semibold">
            {t('locations.availability.exceptionsHeading')}
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNewExceptionOpen((v) => !v)}
          >
            <Plus weight="bold" />
            {t('locations.availability.addException')}
          </Button>
        </div>

        {newExceptionOpen ? (
          <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex flex-col gap-1 text-xs">
                <span>{t('locations.availability.exceptionDate')}</span>
                <DateInputISO
                  value={excDate}
                  onChange={setExcDate}
                  required
                />
              </label>
              <div className="flex gap-2 self-end">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    checked={excKind === 'closed'}
                    onChange={() => setExcKind('closed')}
                  />
                  {t('locations.availability.kindClosed')}
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="radio"
                    checked={excKind === 'hours'}
                    onChange={() => setExcKind('hours')}
                  />
                  {t('locations.availability.kindHours')}
                </label>
              </div>
              {excKind === 'hours' ? (
                <div className="flex items-center gap-2 self-end">
                  <TimeInput24h
                    value={excStart}
                    onChange={setExcStart}
                    className="w-24"
                  />
                  <span className="text-foreground/50">—</span>
                  <TimeInput24h
                    value={excEnd}
                    onChange={setExcEnd}
                    className="w-24"
                  />
                </div>
              ) : null}
            </div>
            <Input
              className="mt-3"
              placeholder={t('locations.availability.exceptionNotePlaceholder')}
              value={excNote}
              onChange={(e) => setExcNote(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNewExceptionOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!excDate || addExcMut.isPending}
                onClick={() => addExcMut.mutate()}
              >
                <Plus weight="bold" />
                {t('locations.availability.addException')}
              </Button>
            </div>
          </div>
        ) : null}

        {exceptions.length === 0 ? (
          <p className="mt-3 text-xs text-foreground/60">
            {t('locations.availability.exceptionsEmpty')}
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {exceptions.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm">
                    <span className="font-medium">{e.on_date}</span>{' '}
                    <span className="text-foreground/60">·</span>{' '}
                    {e.kind === 'closed'
                      ? t('locations.availability.kindClosed')
                      : `${e.start_time} — ${e.end_time}`}
                    {e.note ? (
                      <span className="text-foreground/60"> — {e.note}</span>
                    ) : null}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('common.remove')}
                  disabled={deleteExcMut.isPending}
                  onClick={() => handleDeleteException(e)}
                >
                  <Trash weight="bold" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </fieldset>
  );
});
